import Component from '@glimmer/component';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { trackedFunction } from 'reactiveweb/function';
import { LinkTo } from '@ember/routing';
import { PhTable, PhChartBar } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import ReportStatsModal from './report-stats-modal';
import {
  PRODUCT_LABELS,
  PRODUCT_SECTION_ORDER,
  VALID_REPORT_TYPES,
  type ProductCode,
} from '../utils/dataset-utils';
import type { Store as StoreService } from '@warp-drive/core';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';
import { tracked } from '@glimmer/tracking';
import { query } from '@warp-drive/utilities/json-api';

interface PartnerReportsPageSignature {
  Args: {
    companyId: string;
    viewerRoute: string;
    /**
     * Report types (lowercase product codes) to list. Defaults to every
     * builder product; partner admin passes ["tas"] for the launch scope.
     */
    reportTypes?: string[];
  };
}

export default class PartnerReportsPage extends Component<PartnerReportsPageSignature> {
  @service declare store: StoreService;

  /** Report whose usage-stats modal is open (null = closed) */
  @tracked statsReport: Report | null = null;

  openStats = (report: Report) => {
    this.statsReport = report;
  };

  closeStats = () => {
    this.statsReport = null;
  };

  /**
   * Lowercased, de-duped allow-list of report types, sanitized to the
   * builder-supported product set. FAIL CLOSED: only a truly OMITTED arg
   * (undefined) means "all products" — a provided list that is empty (or
   * sanitizes to empty) allows NOTHING. Never a fallback to all products,
   * and never an empty filter[report_type] param the server might ignore.
   */
  get effectiveReportTypes(): string[] {
    const requested = this.args.reportTypes;
    if (requested === undefined) return [...VALID_REPORT_TYPES];
    return [
      ...new Set(
        requested.map((type) => type.toLowerCase()).filter((type) => VALID_REPORT_TYPES.has(type))
      ),
    ];
  }

  reportsData = trackedFunction(this, async () => {
    if (this.effectiveReportTypes.length === 0) return [];

    const { content } = (await this.store.request(
      query<Report>(
        'report',
        {
          'page[limit]': 200,
          'filter[report_type]': this.effectiveReportTypes.join(','),
        },
        { namespace: 'api/v2' }
      )
    )) as { content: { data: Report[] } };
    return content.data;
  });

  get isLoading() {
    return this.reportsData.isLoading;
  }

  get publishedReports() {
    const all = this.reportsData.value ?? [];
    const companyId = Number(this.args.companyId);
    const allowedTypes = this.effectiveReportTypes;
    return all.filter(
      (r) =>
        allowedTypes.includes((r.reportType || '').toLowerCase()) &&
        r.isPublished &&
        !r.isArchived &&
        r.isVisibleToCompany(companyId)
    );
  }

  get reportsByProduct(): Array<{ product: ProductCode; label: string; reports: Report[] }> {
    const reports = this.publishedReports;
    const grouped = new Map<ProductCode, Report[]>();
    for (const report of reports) {
      const rawType = (report.reportType || '').toUpperCase() as ProductCode;
      const product = PRODUCT_LABELS[rawType] ? rawType : 'Core';
      const list = grouped.get(product) ?? [];
      list.push(report);
      grouped.set(product, list);
    }
    return PRODUCT_SECTION_ORDER.filter((code) => grouped.has(code)).map((code) => ({
      product: code,
      label: PRODUCT_LABELS[code],
      reports: grouped.get(code) ?? [],
    }));
  }

  queryFor = (report: Report) => {
    return {
      reportId: report.id,
      companyId: this.args.companyId,
    };
  };

  static MAX_DESCRIPTION_LENGTH = 120;

  truncateDescription = (report: Report) => {
    const desc = report.description;
    if (!desc) return '';
    if (desc.length <= PartnerReportsPage.MAX_DESCRIPTION_LENGTH) return desc;
    return desc.slice(0, PartnerReportsPage.MAX_DESCRIPTION_LENGTH) + '…';
  };

  <template>
    {{#if this.isLoading}}
      <div class="py-8 text-center tio-fg-muted">{{t "reporting.loading"}}</div>
    {{else if this.publishedReports.length}}
      {{#each this.reportsByProduct as |section|}}
        <div class="tio-col-tight">
          <h2 class="tio-text-lg-semibold tio-fg-primary">{{section.label}}</h2>
          {{#each section.reports as |report|}}
            <div
              class="tio-col-extra-tight rounded-lg border tio-border-default tio-bg-surface px-4 py-3 transition hover:tio-border-info hover:shadow-sm"
            >
              {{! Primary Row: Clickable Name }}
              <div class="tio-row">
                <LinkTo
                  class="group tio-row-tight min-w-0 tio-text-sm-medium tio-fg-info-h hover:tio-fg-info"
                  @route={{@viewerRoute}}
                  @query={{this.queryFor report}}
                >
                  <PhTable class="h-4 w-4 shrink-0 tio-fg-info group-hover:tio-fg-info-h" />
                  <span class="truncate">{{report.name}}</span>
                </LinkTo>
              </div>

              {{! Description }}
              {{#let (this.truncateDescription report) as |desc|}}
                {{#if desc}}
                  <p class="pl-6 text-xs tio-fg-secondary">{{desc}}</p>
                {{/if}}
              {{/let}}

              {{! Secondary Row: Date + Stats }}
              <div class="tio-row-loose pl-6 text-xs tio-fg-muted">
                <span>{{t "reporting.updated" date=report.formattedUpdatedAt}}</span>
                <span class="tio-fg-faint">·</span>
                <button
                  type="button"
                  class="tio-inline-row-extra-tight tio-fg-info hover:tio-fg-info-h"
                  title={{t "reporting.views"}}
                  aria-label="{{t 'reporting.views'}} — {{report.name}}"
                  {{on "click" (fn this.openStats report)}}
                >
                  <PhChartBar class="h-3 w-3" />
                  {{t "reporting.views"}}
                </button>
              </div>
            </div>
          {{/each}}
        </div>
      {{/each}}
    {{else}}
      <div class="py-8 text-center">
        <p class="tio-fg-muted">{{t "reporting.no_reports"}}</p>
      </div>
    {{/if}}

    <ReportStatsModal
      @report={{this.statsReport}}
      @companyId={{@companyId}}
      @onClose={{this.closeStats}}
    />
  </template>
}
